import type { ReactNode } from 'react';
import { Image, Pressable, StyleSheet, View, type ImageSourcePropType } from 'react-native';

import { assertHitOnLayout } from '../a11y';
import { Icon } from '../icons/Icon';
import { avatarDisc, colors, hit, radii, strokes, type AvatarDiscId } from '../tokens';

export interface AvatarDiscProps {
  /** Which pastel disc (neutral avatar id or 'parent'). */
  disc: AvatarDiscId;
  source: ImageSourcePropType;
  size?: number;
  /** Selectable picker mode (P04): 2.5 ink ring + check when selected, 2px ink ring otherwise (board: halo, 52). */
  selectable?: boolean;
  selected?: boolean;
  /** Display mode ring (board: every disc on 1a/P10 has an ink border — 2px, 2.5 at 46 and up). */
  ring?: 'ink' | 'none';
  onPress?: () => void;
  accessibilityLabel: string;
  testID?: string;
  /**
   * **A disc colour from the server** (C33: each panda's own tint), in place of `disc`'s palette entry. `disc` still
   * names the disc for the hit-target assertion.
   */
  discColor?: string;
  /**
   * **Draws the picture instead of `source`** (the pandas, drawn with a disk-caching image), given the exact box the
   * picture fills inside the ring.
   */
  renderImage?: (box: { width: number; height: number; borderRadius: number }) => ReactNode;
}

/** A cub head on its pastel disc (1a rows, kid cards, the P04 picker). The image sits inside the ring. */
export function AvatarDisc({
  disc,
  source,
  size = 44,
  selectable = false,
  selected = false,
  onPress,
  ring = 'ink',
  accessibilityLabel,
  testID,
  discColor,
  renderImage,
}: AvatarDiscProps) {
  const displayRing = ring === 'ink' ? (size >= 46 ? strokes.playfulBold : strokes.playful) : 0;
  const ringWidth = selectable ? (selected ? strokes.playfulBold : strokes.playful) : displayRing;
  const content = (
    <View
      style={[
        styles.disc,
        {
          width: size,
          height: size,
          borderRadius: radii.pill,
          backgroundColor: discColor ?? avatarDisc[disc],
          borderWidth: ringWidth,
          borderColor: colors.ink,
        },
      ]}
    >
      {renderImage ? (
        renderImage({
          width: size - 2 * ringWidth,
          height: size - 2 * ringWidth,
          borderRadius: radii.pill,
        })
      ) : (
        <Image
          source={source}
          style={{
            width: size - 2 * ringWidth,
            height: size - 2 * ringWidth,
            borderRadius: radii.pill,
          }}
          resizeMode="cover"
        />
      )}
      {selectable && selected ? (
        <View style={styles.check}>
          <Icon name="check" size={12} color={colors.white} strokeWidth={3} />
        </View>
      ) : null}
    </View>
  );
  if (!selectable)
    return (
      // `accessible` is what carries the label on iOS: RCTViewComponentView sets isAccessibilityElement
      // from that prop alone, and a role never sets it — the same omission fixed in BadgeTile and TrailNode.
      <View
        accessible
        accessibilityRole="image"
        accessibilityLabel={accessibilityLabel}
        testID={testID}
      >
        {content}
      </View>
    );
  const box = Math.max(size, hit.parent);
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected, checked: selected }}
      accessibilityLabel={accessibilityLabel}
      onLayout={assertHitOnLayout(`AvatarDisc.${disc}`, hit.parent)}
      testID={testID}
      style={[styles.box, { width: box, height: box }]}
    >
      {content}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  box: { alignItems: 'center', justifyContent: 'center' },
  disc: { alignItems: 'center', justifyContent: 'center', overflow: 'visible' },
  check: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 20,
    height: 20,
    borderRadius: radii.pill,
    backgroundColor: colors.ink,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.white,
  },
});
